// components/Compliance/MandatoryTrainingList.js
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShieldCheck, Plus, Edit2, Trash2, BarChart3, PlayCircle, Archive, FilterX,
} from 'lucide-react';
import {
  PageHeader, AppButton, ConfirmModal, StatusBadge, FilterToolbar, AppDataTable,
  ActionMenu, ActionMenuItem, ActionMenuDivider,
  applyColumnFilters, sortRows, isColumnFilterActive,
} from '../ui';
import CommonToaster from '../Common/CommonToaster';
import {
  listCampaigns, deleteCampaign, activateCampaign, archiveCampaign,
} from '../../utils/complianceApi';

/**
 * MandatoryTrainingList — compliance campaigns.
 *
 * The table is `ui/AppDataTable` with the header controls on, the same shape as
 * `/sa/institutes`: `listCampaigns` returns every campaign in one response, so
 * filtering, sorting and paging all happen in the browser over the whole list.
 *
 * ⚠️ The search box and the whole `FilterPanel` drawer are GONE, deliberately.
 * The drawer held one control — a status select — which is now the Status
 * column's own set filter, and the search read one field, which is now the Name
 * column's text filter. Keeping either beside the header controls would mean two
 * places to set the same thing, and two places that will eventually disagree.
 *
 * ⚠️ No `_compliance.scss` import. The sheet stays for the sibling Compliance
 * components — and for `SuperAdmin/ZoomHosts/CapacityPlannerTab`, which renders
 * its classes while importing no stylesheet of its own.
 */

const PAGE_SIZE = 25;

const DASH = <span className="tw-text-content-subtle">—</span>;

const STATUS_FILTER_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
];

const AUDIENCE_FILTER_OPTIONS = [
  { value: 'all', label: 'All employees' },
  { value: 'groups', label: 'Specific groups' },
];

/** campaign.status → a word the badge vocabulary knows. */
const STATUS_TONE = { active: 'active', archived: 'inactive', draft: 'pending' };

const groupCount = (c) => (c.audience?.groupIds || []).length;
const isAllEmployees = (c) => Boolean(c.audience?.allActiveStudents);

const CONFIRM_CONFIG = {
  delete: {
    title: 'Delete campaign?', message: 'This action cannot be undone.',
    confirmText: 'Delete', variant: 'danger', done: 'Campaign deleted.',
  },
  activate: {
    title: 'Activate campaign?', message: 'Assignments will be created and notifications dispatched.',
    confirmText: 'Activate', variant: 'success', done: 'Campaign activated.',
  },
  archive: {
    title: 'Archive campaign?', message: 'Active assignments will stop sending reminders.',
    confirmText: 'Archive', variant: 'warning', done: 'Campaign archived.',
  },
};

const ACTIONS = {
  delete: deleteCampaign,
  activate: activateCampaign,
  archive: archiveCampaign,
};

export default function MandatoryTrainingList() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null); // { kind, item }
  const [working, setWorking] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const [columnFilters, setColumnFilters] = useState({});
  const [sort, setSort] = useState(null);
  const [page, setPage] = useState(1);

  const showToast = useCallback(
    (message, type = 'success') => setToast({ show: true, message, type }),
    [],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listCampaigns();
      setCampaigns(data?.campaigns || data?.items || []);
    } catch {
      showToast('Failed to load campaigns.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const columns = useMemo(() => [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      filter: { type: 'text', placeholder: 'Campaign name…' },
      filterValue: (c) => c.name || '',
      render: (c) => <span className="tw-font-medium tw-text-content">{c.name}</span>,
    },
    {
      key: 'course',
      label: 'Course',
      sortable: true,
      filter: 'text',
      // The API has used both keys; reading one would make the filter miss rows
      // whose course name is plainly on screen.
      filterValue: (c) => c.course?.name || c.course?.title || '',
      render: (c) => c.course?.name || c.course?.title || DASH,
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      filter: { type: 'set', options: STATUS_FILTER_OPTIONS },
      filterValue: (c) => c.status || 'draft',
      render: (c) => (
        <StatusBadge status={STATUS_TONE[c.status] || 'pending'}>{c.status}</StatusBadge>
      ),
    },
    {
      key: 'dueDate',
      label: 'Due Date',
      sortable: true,
      filter: 'date',
      filterValue: (c) => c.dueDate || null,
      // ⚠️ IST explicitly. The box is UTC and a bare toLocaleDateString names
      // the previous day for any due date set after 05:30 IST.
      render: (c) => (c.dueDate
        ? new Date(c.dueDate).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })
        : DASH),
    },
    {
      key: 'audience',
      label: 'Audience',
      sortable: true,
      filter: { type: 'set', options: AUDIENCE_FILTER_OPTIONS },
      filterValue: (c) => (isAllEmployees(c) ? 'all' : 'groups'),
      // "Everyone" is the widest audience, so it sorts above any group count.
      sortValue: (c) => (isAllEmployees(c) ? Infinity : groupCount(c)),
      render: (c) => (isAllEmployees(c)
        ? 'All employees'
        : `${groupCount(c)} group${groupCount(c) === 1 ? '' : 's'}`),
    },
    {
      key: 'actions',
      label: '',
      width: 48,
      pinned: 'right',
      resizable: false,
      // Five icon buttons became one menu: the row's actions are conditional on
      // status, and a cell whose control count changes per row is a column that
      // never lines up.
      //
      // ⚠️ The row is clickable, so every control in this cell must stop the
      // click reaching it — otherwise opening the menu also navigates away.
      render: (c) => (
        <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">
          <ActionMenu label={`Actions for ${c.name}`}>
            <ActionMenuItem onClick={() => navigate(`/compliance/mandatory-training/${c._id}/report`)}>
              <BarChart3 size={14} /> Report
            </ActionMenuItem>
            <ActionMenuItem onClick={() => navigate(`/compliance/mandatory-training/${c._id}`)}>
              <Edit2 size={14} /> Edit
            </ActionMenuItem>
            {c.status === 'draft' && (
              <ActionMenuItem onClick={() => setConfirm({ kind: 'activate', item: c })}>
                <PlayCircle size={14} /> Activate
              </ActionMenuItem>
            )}
            {c.status === 'active' && (
              <ActionMenuItem onClick={() => setConfirm({ kind: 'archive', item: c })}>
                <Archive size={14} /> Archive
              </ActionMenuItem>
            )}
            <ActionMenuDivider />
            <ActionMenuItem danger onClick={() => setConfirm({ kind: 'delete', item: c })}>
              <Trash2 size={14} /> Delete
            </ActionMenuItem>
          </ActionMenu>
        </div>
      ),
    },
  ], [navigate]);

  const filtersOn = Object.values(columnFilters).some(isColumnFilterActive);

  const visible = useMemo(
    () => sortRows(applyColumnFilters(campaigns, columns, columnFilters), columns, sort),
    [campaigns, columns, columnFilters, sort],
  );

  // A filter or a sort can leave the current page past the end of the results.
  useEffect(() => { setPage(1); }, [columnFilters, sort]);
  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const pageRows = useMemo(
    () => visible.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [visible, page],
  );

  const confirmConfig = confirm ? CONFIRM_CONFIG[confirm.kind] : null;

  const onConfirm = async () => {
    if (!confirm) return;
    const { kind, item } = confirm;
    try {
      setWorking(true);
      await ACTIONS[kind](item._id);
      setConfirm(null);
      showToast(CONFIRM_CONFIG[kind].done);
      load();
    } catch (err) {
      // The previous version swallowed this in a `finally` and reloaded as if
      // it had worked — a failed activate looked identical to a successful one.
      showToast(err?.response?.data?.message || `Could not ${kind} the campaign.`, 'error');
      setConfirm(null);
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-6">
      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />

      <PageHeader
        title="Mandatory Training"
        icon={ShieldCheck}
        backButton
      />

      <AppDataTable
        fill={false}
        resizable
        storageKey="compliance-mandatory-training"
        toolbar={
          <FilterToolbar
            flush
            actions={
              <AppButton size="sm" onClick={() => navigate('/compliance/mandatory-training/new')}>
                <Plus size={16} /> New Campaign
              </AppButton>
            }
          >
            <span className="tw-text-[0.8125rem] tw-text-content-subtle">
              {visible.length === campaigns.length
                ? `${campaigns.length} campaign${campaigns.length === 1 ? '' : 's'}`
                : `${visible.length} of ${campaigns.length}`}
            </span>
            {/* A funnel in a header is easy to set and easy to forget — this is
                the only thing on screen that says a column is hiding rows. */}
            {filtersOn && (
              <AppButton size="sm" variant="outline" onClick={() => setColumnFilters({})}>
                <FilterX size={14} /> Clear filters
              </AppButton>
            )}
          </FilterToolbar>
        }
        columns={columns}
        rows={pageRows}
        rowKey={(c) => c._id}
        loading={loading}
        sort={sort}
        onSortChange={(key, direction) => setSort({ key, direction })}
        columnFilters={columnFilters}
        onColumnFilterChange={(key, value) =>
          setColumnFilters((prev) => {
            // Cleared filters are REMOVED, not stored as undefined — a key
            // sitting there holding nothing would keep "Clear filters" on
            // screen after the last one was cleared.
            const next = { ...prev };
            if (value === undefined) delete next[key];
            else next[key] = value;
            return next;
          })
        }
        onRowClick={(c) => navigate(`/compliance/mandatory-training/${c._id}`)}
        emptyMessage={filtersOn
          ? 'No campaign matches these filters.'
          : 'No campaigns yet. Create one to assign mandatory training.'}
        pagination={totalPages > 1 ? {
          page,
          totalPages,
          total: visible.length,
          pageSize: PAGE_SIZE,
          onChange: setPage,
          infoLabel: 'campaigns',
        } : undefined}
      />

      {confirmConfig && (
        <ConfirmModal
          show={!!confirm}
          title={confirmConfig.title}
          message={confirmConfig.message}
          confirmText={confirmConfig.confirmText}
          variant={confirmConfig.variant}
          loading={working}
          onConfirm={onConfirm}
          onHide={() => setConfirm(null)}
        />
      )}
    </div>
  );
}
